import axios from 'axios';
import { header } from '../Utils/ApiHeader';

import * as Config from "../Utils/Config";

class DeltaExchangeService {
  
  /**
   * Fetch historical data from Delta Exchange
   * @param {Object} params - Parameters for historical data
   * @param {string} params.symbol - Trading symbol (e.g., BTCUSD)
   * @param {string} params.timeframe - Timeframe (1m, 5m, 15m, 1h, 4h, 1d)
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Historical data response
   */
  static async getHistoricalData(params) {
    try {
      const response = await axios.post(
        `${Config.base_url}historical-data`,
        params,
        {
          headers: header(),
          timeout: 300000 // 5 minutes timeout for large datasets (up to 5 years)
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch historical data');
    }
  }

  /**
   * Get available trading symbols from Delta Exchange
   * @returns {Promise<Object>} Available symbols
   */
  static async getAvailableSymbols() {
    try {
      const response = await axios.get(
        `${Config.base_url}symbols`,
        {
          headers: header(),
          timeout: 15000
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching symbols:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch available symbols');
    }
  }

  /**
   * Run backtest simulation
   * @param {Object} backtestParams - Backtest parameters
   * @returns {Promise<Object>} Backtest results
   */
  static async runBacktest(backtestParams) {
    try {
      const response = await axios.post(
        `${Config.base_url}/api/backtest/run`,
        backtestParams,
        {
          headers: header(),
          timeout: 120000 // 2 minute timeout for backtesting
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error running backtest:', error);
      throw new Error(error.response?.data?.message || 'Failed to run backtest');
    }
  }

  /**
   * Get market data for a specific symbol
   * @param {string} symbol - Trading symbol
   * @returns {Promise<Object>} Market data
   */
  static async getMarketData(symbol) {
    try {
      const response = await axios.get(
        `${Config.base_url}market-data/${symbol}`,
        {
          headers: header(),
          timeout: 10000
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching market data:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch market data');
    }
  }

  /**
   * Get trading statistics for a symbol
   * @param {string} symbol - Trading symbol
   * @param {string} timeframe - Timeframe
   * @returns {Promise<Object>} Trading statistics
   */
  static async getTradingStats(symbol, timeframe = '1h') {
    try {
      const response = await axios.get(
        `${Config.base_url}stats/${symbol}`,
        {
          params: { timeframe },
          headers: header(),
          timeout: 10000
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching trading stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch trading statistics');
    }
  }

  /**
   * Validate backtest parameters
   * @param {Object} params - Backtest parameters
   * @returns {Object} Validation result
   */
  static validateBacktestParams(params) {
    const errors = [];
    
    if (!params.symbol) {
      errors.push('Symbol is required');
    }
    
    if (!params.timeframe) {
      errors.push('Timeframe is required');
    }
    
    if (!params.startDate) {
      errors.push('Start date is required');
    }
    
    if (!params.endDate) {
      errors.push('End date is required');
    }
    
    if (params.startDate && params.endDate) {
      const start = new Date(params.startDate);
      const end = new Date(params.endDate);
      
      if (start >= end) {
        errors.push('Start date must be before end date');
      }
      
      const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
      const maxDays = 1825; // 5 years (5 * 365 days)
      if (daysDiff > maxDays) {
        errors.push(`Date range cannot exceed ${maxDays} days (5 years)`);
      }
      
      // Warning for very large date ranges (but allow it)
      if (daysDiff > 365 && daysDiff <= maxDays) {
        console.warn(`Large date range detected: ${Math.ceil(daysDiff)} days. This may take longer to process.`);
      }
    }
    
    if (!params.strategy) {
      errors.push('Strategy is required');
    }
    
    if (!params.initialCapital || params.initialCapital < 1000) {
      errors.push('Initial capital must be at least $1000');
    }
    
    if (!params.riskPerTrade || params.riskPerTrade < 0.1 || params.riskPerTrade > 10) {
      errors.push('Risk per trade must be between 0.1% and 10%');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Calculate optimal parameters for backtesting
   * @param {Object} params - Basic parameters
   * @returns {Object} Optimized parameters
   */
  static optimizeBacktestParams(params) {
    const optimized = { ...params };
    
    // Set default timeframe based on strategy
    const strategyTimeframes = {
      'scalping': '1m',
      'momentum': '5m',
      'trend_following': '1h',
      'mean_reversion': '4h',
      'breakout': '1d'
    };
    
    if (!optimized.timeframe && optimized.strategy) {
      optimized.timeframe = strategyTimeframes[optimized.strategy] || '1h';
    }
    
    // Set default risk per trade
    if (!optimized.riskPerTrade) {
      optimized.riskPerTrade = 2; // 2% default
    }
    
    // Set default initial capital
    if (!optimized.initialCapital) {
      optimized.initialCapital = 10000; // $10,000 default
    }
    
    // Set default date range if not provided
    if (!optimized.startDate || !optimized.endDate) {
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
      
      optimized.endDate = endDate.toISOString().split('T')[0];
      optimized.startDate = startDate.toISOString().split('T')[0];
    }
    
    return optimized;
  }

  /**
   * Format backtest results for display
   * @param {Object} results - Raw backtest results
   * @returns {Object} Formatted results
   */
  static formatBacktestResults(results) {
    if (!results) return null;
    
    return {
      ...results,
      totalReturn: parseFloat(results.totalReturn?.toFixed(2) || 0),
      winRate: parseFloat(results.winRate?.toFixed(2) || 0),
      maxDrawdown: parseFloat(results.maxDrawdown?.toFixed(2) || 0),
      sharpeRatio: parseFloat(results.sharpeRatio?.toFixed(2) || 0),
      finalCapital: parseFloat(results.finalCapital?.toFixed(2) || 0),
      profitLoss: parseFloat((results.finalCapital - results.initialCapital).toFixed(2)),
      avgTradeReturn: results.totalTrades > 0 ? 
        parseFloat(((results.finalCapital - results.initialCapital) / results.totalTrades).toFixed(2)) : 0,
      profitFactor: results.winningTrades > 0 && results.totalTrades > results.winningTrades ?
        parseFloat((results.winningTrades / (results.totalTrades - results.winningTrades)).toFixed(2)) : 0
    };
  }
}

export default DeltaExchangeService;
